<template>
  <template v-for="item in sortedItems" :key="item.uuid">
    <a
      v-if="item.item_type === 'external' && depth > 0"
      :href="item.external_url ?? '#'"
      target="_blank"
      rel="noopener noreferrer"
      :class="nestedExternalLinkClass"
      :aria-label="`${item.display_label} (opens HubSpot in a new tab)`"
    >
      <span class="text-left truncate">{{ item.display_label }}</span>
    </a>

    <div v-else-if="item.item_type === 'external'" :class="rootItemClass">
      <a
        :href="item.external_url ?? '#'"
        target="_blank"
        rel="noopener noreferrer"
        class="group w-full flex items-center justify-between gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 rounded-b-none text-dark-text-primary hover:bg-surface-hover no-underline"
        :aria-label="`${item.display_label} (opens in a new tab)`"
      >
        <div class="flex items-center text-left">
          <div class="mr-2">
            <BaseSidebarSectionIcon :section-id="item.key" />
          </div>
          <div class="text-left">
            <span class="font-medium">{{ item.display_label }}</span>
          </div>
        </div>
        <i
          class="ri-arrow-right-s-line transition-transform duration-200 flex-shrink-0 text-text-secondary"
          aria-hidden="true"
        ></i>
      </a>
    </div>

    <div v-else-if="item.item_type === 'section'" :class="rootItemClass">
      <BaseDisclosure
        :open="folderOpen(item.key)"
        @update:open="setFolderOpen(item.key, $event)"
        :aria-label="item.display_label"
        chevron="arrow-right"
      >
        <template #icon>
          <BaseSidebarSectionIcon :section-id="item.key" />
        </template>
        <template #header>
          <span class="font-medium">{{ item.display_label }}</span>
        </template>
        <template #content>
          <div :class="childrenContainerClass">
            <NavigationTreeRenderer
              :items="item.children ?? []"
              :depth="depth + 1"
              :flyout="flyout"
            />
          </div>
        </template>
      </BaseDisclosure>
    </div>

    <div
      v-else-if="item.item_type === 'group'"
      :class="groupWrapperClass(item.key)"
    >
      <BaseDisclosure
        :open="folderOpen(item.key)"
        @update:open="setFolderOpen(item.key, $event)"
        :aria-label="item.display_label"
        nested
      >
        <template #header>
          <span class="font-medium">{{ item.display_label }}</span>
        </template>
        <template #content>
          <div :class="nestedLevelClass">
            <NavigationTreeRenderer
              :items="item.children ?? []"
              :depth="depth + 1"
              :flyout="flyout"
            />
          </div>
        </template>
      </BaseDisclosure>
    </div>

    <BaseDisclosureItem
      v-else-if="item.item_type === 'link' && item.metadata?.disabled === true"
      disabled
      :sub-sub="depth > 1"
    >
      <template #content>
        <span>{{ item.display_label }}</span>
      </template>
    </BaseDisclosureItem>

    <BaseDisclosureItem
      v-else-if="item.item_type === 'link' && item.route_path"
      :to="item.route_path"
      :active="isActiveNavigationRoute(route.path, item.route_path)"
      :sub-sub="depth > 1"
    >
      <template #content>
        <span>{{ item.display_label }}</span>
      </template>
    </BaseDisclosureItem>
  </template>
</template>

<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import type { NavigationItem } from '@/types/navigation'
import BaseDisclosure from '@/components/ui/BaseDisclosure.vue'
import BaseDisclosureItem from '@/components/ui/BaseDisclosureItem.vue'
import BaseSidebarSectionIcon from '@/components/layout/BaseSidebarSectionIcon.vue'
import { navigationFolderKey } from '@/utils/navigationFolderKey'
import { isActiveNavigationRoute, sortNavigationChildren } from '@/utils/navigationTreeUtils'

const props = withDefaults(
  defineProps<{
    items: NavigationItem[]
    depth?: number
    flyout?: boolean
  }>(),
  {
    depth: 0,
    flyout: false,
  },
)

const route = useRoute()
const openFolders = inject<Ref<Record<string, boolean>>>('sidebarOpenFolders', ref({}))
const sidebarNavDense = inject<boolean>('sidebarNavDense', false)

const nestedExternalLinkClass = computed(() => {
  const padding = sidebarNavDense ? 'px-3 py-1.5' : 'px-3 py-2'
  const textSize = sidebarNavDense ? 'text-[13px]' : 'text-sm'
  return [
    'group flex items-center gap-2 flex-1 rounded-md transition-colors duration-200 w-full',
    padding,
    textSize,
    'font-medium text-dark-text-primary hover:bg-surface-hover no-underline',
  ]
})

const sortedItems = computed(() => sortNavigationChildren(props.items, props.depth === 0))

const rootItemClass = computed(() => (props.depth === 0 && !props.flyout ? 'mt-2' : ''))

const nestedLevelClass = computed(() =>
  props.flyout ? 'ml-3 py-1 space-y-0.5' : 'ml-3 mt-1 space-y-0.5',
)

const childrenContainerClass = computed(() =>
  props.flyout ? 'ml-4 space-y-1 py-2' : 'ml-4 mt-1 space-y-0.5',
)

function folderOpen(itemKey: string): boolean {
  const key = navigationFolderKey(itemKey)
  return openFolders.value[key] ?? false
}

function setFolderOpen(itemKey: string, open: boolean): void {
  const key = navigationFolderKey(itemKey)
  openFolders.value = { ...openFolders.value, [key]: open }
}

function groupWrapperClass(itemKey: string): string {
  return itemKey === 'operations.reports'
    ? 'reports-disclosure-wrapper'
    : 'site-surveys-disclosure-wrapper'
}
</script>

<style scoped>
.site-surveys-disclosure-wrapper :deep(button) {
  background-color: transparent !important;
  border-bottom: 1px solid var(--color-border);
  border-radius: 0;
}
.site-surveys-disclosure-wrapper :deep(button:hover) {
  background-color: transparent !important;
}
.site-surveys-disclosure-wrapper :deep(button[aria-expanded='true']) {
  background-color: transparent !important;
  border-bottom: 1px solid var(--color-border);
}
.reports-disclosure-wrapper :deep(button) {
  background-color: transparent !important;
  border-bottom: 1px solid var(--color-border);
  border-radius: 0;
}
.reports-disclosure-wrapper :deep(button:hover) {
  background-color: transparent !important;
}
.reports-disclosure-wrapper :deep(button[aria-expanded='true']) {
  background-color: transparent !important;
  border-bottom: 1px solid var(--color-border);
}
</style>
